import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import colours from '../../../styles/appColours';
import AppLayout from '../../../layouts/AdminLayout';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import AddEditSubscription from './AddEditSubscription';
import SubscribeAction from './SubscribeAction';
import { useRoles } from '../../../hooks/useRoles';
import { useLocation, useNavigate } from 'react-router-dom';
import { BsFilter, BsSearch } from 'react-icons/bs';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

export const SubscriptionPage = () => {
	const { scope } = useRoles();
	const [selectedPlan, setSelectedPlan] = useState<any>(null);
	const Baseurl = process.env.REACT_APP_BASEURL;
	const getToken: any = localStorage.getItem('eftd-admin-token');
	const tokenObject: any = JSON.parse(getToken);
	const token = tokenObject?.data?.data?.token;
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};

	const location = useLocation();
	const navigate = useNavigate();
	const queryParams = new URLSearchParams(location.search);
	const currentPage = Number(queryParams.get('page')) || 1;

	const queryClient = useQueryClient();
	const [searchFilter, setSearchFilter] = useState<any>();
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
	const [limit, setLimit] = useState(20);
	const [totalPages, setTotalPages] = useState(1);

	// Debounce search input
	useEffect(() => {
		const timerId = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 500);

		return () => {
			clearTimeout(timerId);
		};
	}, [searchQuery]);

	const getSubscriptions = async () => {
		try {
			let url = `${Baseurl}/api/admin/subscription/subscribers?limit=${limit}&page=${currentPage}`;

			if (debouncedSearchQuery) {
				url += `&query=${encodeURIComponent(debouncedSearchQuery)}`;
			}

			const response = await axios.get(url, config);
			const data = response?.data?.data;

			// Set total pages for pagination
			if (data?.meta?.total) {
				setTotalPages(Math.ceil(data.meta.total / limit));
			}

			// Return empty array if data is null/undefined or not an array
			return Array.isArray(data?.items) ? data.items : [];
		} catch (error: any) {
			if (error?.response?.status === 404) {
				return [];
			}
			console.error('Failed to fetch customers:', error);
			return [];
		}
	};

	const getPlans = async () => {
		try {
			const response = await axios.get(`${Baseurl}/api/admin/subscription/getplans`, config);
			const data = response?.data?.data;
			return Array.isArray(data) ? data : [];
		} catch (error: any) {
			if (error?.response?.status === 404) return [];
			console.error('Failed to fetch plans:', error);
			return [];
		}
	};

	const [selectedTab, setSelectedTab] = useState('SUBSCRIPTIONS');

	const {
		data: allSubscriptions,
		isLoading,
		isRefetching,
		refetch: refetchSubscription,
	} = useQuery(['SUBSCRIPTIONS', debouncedSearchQuery, currentPage, limit], getSubscriptions, {
		enabled: selectedTab === 'SUBSCRIPTIONS',
		keepPreviousData: true,
	});

	const { data: allPlans = [], isLoading: loadingPlans } = useQuery(['PLANS'], getPlans, {
		enabled: selectedTab === 'PLANS',
	});

	const customerTabs = [
		{ label: 'Subscriptions', value: 'SUBSCRIPTIONS' },
		{ label: 'Plans', value: 'PLANS' },
	];

	const filteredSubscriptions = allSubscriptions?.filter((customer: any) => {
		if (selectedTab === 'SUBSCRIPTIONS') return true;
		return customer.accountType?.toUpperCase() === selectedTab;
	});

	const [isSubOpen, setSubOpen] = useState(false);
	const [selectedSubscription, setSubDDetails] = useState<any>({});
	const [isPlanOpen, setIsPlanOpen] = useState(false);
	const [isEdit, setEdit] = useState(false);

	const updatePlan = (newPlanObject: any) => {
		const queryKey = ['PLANS'];

		if (!isEdit) {
			const data = newPlanObject?.data?.data;
			// Optimistically update the cache
			queryClient.setQueryData(queryKey, (oldPlans: any) => {
				return oldPlans ? [...oldPlans, newPlanObject] : [newPlanObject];
			});

			queryClient.invalidateQueries(['PLANS']);
		} else {
			const updatedPlanData = newPlanObject?.data?.data || selectedPlan;

			queryClient.setQueryData(queryKey, (oldPlans: any) => {
				const currentPlans = oldPlans || []; // Ensure oldPlans is an array

				// Find the plan by its ID and replace it
				return currentPlans.map((plan: any) => (plan.id === updatedPlanData.id ? updatedPlanData : plan));
			});

			// Invalidate to potentially trigger a background refetch for consistency
			queryClient.invalidateQueries(['PLANS']);
		}
	};

	const deletePlan = async (planId: string) => {
		if (!window.confirm('Are you sure you want to delete this plan? This action cannot be undone.')) {
			return; // Stop if user cancels
		}

		const toastId = toast.loading('Deleting plan...');

		try {
			if (!config) {
				toast.dismiss(toastId);
				return;
			}

			const response = await axios.delete(`${Baseurl}/api/admin/subscription/deleteplan/${planId}`, config);

			if (response.status === 200 || response.status === 204) {
				// 200 OK or 204 No Content
				toast.success(`Plan "${planId}" deleted successfully!`, { id: toastId });
				// Invalidate the cache for 'PLANS' query to refetch the updated list
				queryClient.invalidateQueries(['PLANS']);
			} else {
				// Handle other successful but unexpected status codes from server
				toast.error(`Failed to delete plan: Server responded with status ${response.status}`, {
					id: toastId,
				});
			}
		} catch (error: any) {
			console.error('Error deleting plan:', error);
			toast.error('Unable to delete plan, you still have active subscribers.', {
				id: toastId,
				duration: 2000,
			});
		} finally {
		}
	};

	const togglePlanStatus = async (planId: string, currentStatus: boolean) => {
		// Determine the action (activate/deactivate) for dynamic messages
		const action = currentStatus ? 'deactivate' : 'activate';
		const confirmationMessage = `Are you sure you want to ${action} this plan?`;
		const loadingMessage = `${action.charAt(0).toUpperCase() + action.slice(1)}ing plan...`; // Capitalize first letter
		const successMessage = `Plan successfully ${action}d!`; // "activated" or "deactivated"
		const errorMessage = `Failed to ${action} plan.`;

		if (!window.confirm(confirmationMessage)) {
			return; // Stop if user cancels
		}

		const toastId = toast.loading(loadingMessage); // Show loading toast

		try {
			// const config = getAuthHeaders();
			if (!config) {
				toast.dismiss(toastId); // Dismiss loading toast if config fails
				return;
			}
			const response = await axios.patch(
				`${Baseurl}/api/admin/subscription/${planId}/toggle/visibility`,
				{}, // Empty body for PATCH if the endpoint is purely a toggle
				config,
			);

			if (response.status === 200) {
				// PATCH requests typically return 200 OK
				toast.success(successMessage, { id: toastId });
				// Invalidate the cache for 'PLANS' query to refetch the updated list
				queryClient.invalidateQueries(['PLANS']);
			} else {
				// Handle other successful but unexpected status codes
				toast.error(`${errorMessage} Server responded with status ${response.status}`, { id: toastId });
			}
		} catch (error: any) {
			console.error('Error toggling plan status:', error);
		}
	};

	useEffect(() => {
		if (!isRefetching) {
			const selected = allSubscriptions?.filter((sub: any) => sub.id === selectedSubscription.id);
			if (selected?.length! > 0) {
				setSubDDetails(selected![0]);
			}
		}
	}, [allSubscriptions, isRefetching, selectedSubscription.id]);

	const handlePageChange = (newPage: number) => {
		navigate(`?page=${newPage}`);
	};

	return (
		<AppLayout>
			<div className="px-5">
				<div className="flex items-center justify-between !py-10">
					<div>
						<h3 className="!text-[28px] !font-bold !text-[#272835]">Subscription Management</h3>
						<p className="!text-sm !text-[#818898]">View and track all your subscriptions details below</p>
					</div>
					{scope === 'write' && (
						<button
							onClick={() => {
								setSelectedPlan({});
								setIsPlanOpen(true);
								setEdit(false);
							}}
							className="!text-sm !font-bold !text-white px-4 py-3 bg-[#f27f1f] rounded-[10px]">
							Create Subscription
						</button>
					)}
				</div>

				{/* Tabs */}
				<div className="flex justify-between border-b border-gray-200 mb-4">
					<div className="flex space-x-4">
						{customerTabs.map((tab) => (
							<button
								key={tab.value}
								onClick={() => setSelectedTab(tab.value)}
								className={`text-sm font-semibold pb-3 border-b-2 ${
									selectedTab === tab.value
										? 'text-gray-700 border-gray-700'
										: 'text-gray-400 border-transparent'
								}`}>
								{tab.label}
							</button>
						))}
					</div>
				</div>

				{/* Tab Content */}
				{selectedTab === 'SUBSCRIPTIONS' && (
					<>
						<div className="flex items-center justify-between mb-4">
							<div className="relative h-12 bg-gray-50 border rounded-lg w-96 border-gray-300">
								<input
									type="text"
									placeholder="Search subscriptions by ID or email"
									className="w-full h-full pl-12 text-sm bg-transparent outline-none placeholder:text-gray-400"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
								/>
								<BsSearch className="absolute left-4 top-3.5 text-gray-400" />
							</div>
						</div>

						{isLoading ? (
							<div className="text-center py-6 text-gray-400">Loading subscriptions...</div>
						) : allSubscriptions?.length === 0 ? (
							<div className="text-center py-6 text-gray-400">No subscriptions found.</div>
						) : (
							<>
								<div className="border border-gray-100 rounded-[20px] pb-3 overflow-auto">
									<Table className="text-left !w-full">
										{/* Table Header */}
										<thead className="bg-gray-100">
											<RowHeader className="!bg-gray-100 text-left pl-0">
												<IDHeader className="!text-left">Subscriber</IDHeader>
												<IDHeader className="!text-left !pl-0">Email</IDHeader>
												<IDHeader className="!text-left !pl-0">Transaction ID</IDHeader>
												<IDHeader className="!text-left !pl-0">Amount</IDHeader>
												<IDHeader className="!text-left !pl-0">Plan</IDHeader>
												<IDHeader className="!text-left !pl-0">Status</IDHeader>
												<IDHeader className="!text-left !pl-0">Action</IDHeader>
											</RowHeader>
										</thead>

										{/* Table Body */}
										<tbody>
											{allSubscriptions?.map((item: any, index: number) => (
												<Row
													key={index}
													className="border-b border-gray-100 hover:bg-gray-100 transition-colors text-left pl-0">
													<ID className="!text-left">
														{item.user?.businessName || item.user?.businessContactInfo?.fullName || 'N/A'}
													</ID>
													<ID className="!text-left !pl-0">{item.user?.email || 'N/A'}</ID>
													<ID className="!text-left !pl-0">{item.id}</ID>
													<ID className="!text-left !pl-0">
														₦{parseInt(item?.plan?.amount).toLocaleString()}
													</ID>
													<ID className="!text-left !pl-0">{item.plan ? item.plan.name : 'N/A'}</ID>
													<ID className="!text-left !pl-0">{item.status}</ID>
													<View>
														<button
															style={{ color: `${colours.orange}` }}
															onClick={() => {
																setSubOpen(true);
																setSubDDetails(item);
															}}>
															View more
														</button>
													</View>
												</Row>
											))}
										</tbody>
									</Table>
								</div>

								{/* Pagination */}
								<div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 mt-4">
									<div className="flex items-center gap-6">
										<p className="text-sm text-gray-500">
											Showing page {currentPage} of {totalPages}
										</p>
										<div className="flex gap-2">
											<button
												onClick={() => handlePageChange(currentPage - 1)}
												disabled={currentPage === 1}
												className={`px-4 py-2 text-sm font-medium rounded-md border ${
													currentPage === 1
														? 'bg-gray-100 text-gray-400 cursor-not-allowed'
														: 'bg-white text-gray-700 hover:bg-gray-50'
												}`}>
												Previous
											</button>
											<button
												onClick={() => handlePageChange(currentPage + 1)}
												disabled={currentPage === totalPages}
												className={`px-4 py-2 text-sm font-medium rounded-md border ${
													currentPage === totalPages
														? 'bg-gray-100 text-gray-400 cursor-not-allowed'
														: 'bg-white text-gray-700 hover:bg-gray-50'
												}`}>
												Next
											</button>
										</div>
									</div>

									<div className="flex items-center gap-2">
										<p className="text-sm text-gray-500">Items per page:</p>

										<select
											onChange={(e) => {
												setLimit(Number(e.target.value));
												handlePageChange(1); // Reset to first page when limit changes
											}}
											className="border border-gray-500 rounded py-1 px-2 text-sm text-gray-500"
											value={limit}>
											<option value="10">10</option>
											<option value="20">20</option>
											<option value="30">30</option>
											<option value="40">40</option>
											<option value="50">50</option>
										</select>
									</div>
								</div>
							</>
						)}
					</>
				)}

				{selectedTab === 'PLANS' && (
					<>
						{loadingPlans ? (
							<div className="text-center py-6 text-gray-400">Loading plans...</div>
						) : allPlans?.length === 0 ? (
							<div className="text-center py-6 text-gray-400">No plans found.</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-7 mt-5">
								{allPlans.map((plan: any) => (
									<div key={plan.id} className="p-6 shadow bg-gray-50 rounded-[20px]">
										<span className="text-sm text-gray-500">{plan.name}</span>
										<h3 className="text-2xl font-bold text-black">
											₦{parseInt(plan.amount).toLocaleString()}/{plan.validity}
										</h3>

										<p className="mt-2 text-gray-400 text-ms">{plan?.description}</p>

										<p className="text-greyscale-700 text-xs font-semibold mt-1">
											Allowed Deliveries {plan.allowedDeliveries}
										</p>

										<div className="w-full border-t border-dashed border-gray-500 mt-7 my-5"></div>

										<div className="flex items-center space-x-3">
											<button
												onClick={() => togglePlanStatus(plan.id, plan.visible)}
												className={`!text-sm !font-bold px-4 py-3 rounded-[10px] transition duration-200
                        ${
													plan.visible
														? 'bg-gray-500 text-white hover:bg-gray-400'
														: 'bg-[#f27f1f] text-white hover:bg-[#f27f1f]'
												}`}>
												{plan.visible ? 'Deactivate' : 'Activate'}
											</button>

											<button
												onClick={() => {
													setSelectedPlan(plan);
													setIsPlanOpen(true);
													setEdit(true);
												}}
												className="!text-sm !font-bold px-4 py-3 border border-[#f27f1f] text-[#f27f1f] rounded-[10px]">
												Edit Plan
											</button>

											<button
												onClick={() => deletePlan(plan.id)}
												className="p-3 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-full transition duration-200"
												title="Delete Plan">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
													strokeWidth="1.5"
													stroke="currentColor"
													className="w-5 h-5">
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.924a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m-1.022.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.924a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m-1.022.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.924a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165"
													/>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M20.25 7.5l-.625 10.625a2.25 2.25 0 01-2.244 2.077H8.924a2.25 2.25 0 01-2.244-2.077L3.75 7.5m6.75-3.004H12c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-3.75c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
													/>
												</svg>
											</button>
										</div>
									</div>
								))}
							</div>
						)}
					</>
				)}
			</div>

			{/* create & edit plan  */}
			<Dialog open={isPlanOpen} onClose={() => {}} className="relative z-50">
				<div className="fixed inset-0 bg-black/30" aria-hidden="true" />

				<div className="fixed inset-0 flex justify-end z-50">
					<Dialog.Panel className="w-full max-w-md h-full bg-white shadow-xl overflow-y-auto">
						<AddEditSubscription
							defaultValues={selectedPlan}
							onClose={() => setIsPlanOpen(false)}
							onUpdate={(data) => updatePlan(data)}
						/>
					</Dialog.Panel>
				</div>
			</Dialog>

			{/* view subscriptions */}
			<Dialog open={isSubOpen} onClose={() => setSubOpen(false)} className="relative z-50">
				<div className="fixed inset-0 bg-black/30" aria-hidden="true" />
				<div className="fixed inset-0 flex items-center justify-center p-4 z-50">
					<Dialog.Panel className="w-full max-w-md min-w-[800px] h-auto bg-white shadow-xl rounded-lg overflow-y-auto">
						{' '}
						{/* Added min-w-[800px] here */}
						<SubscribeAction
							subscriptionData={selectedSubscription}
							refetchSubscription={refetchSubscription}
						/>
					</Dialog.Panel>
				</div>
			</Dialog>
		</AppLayout>
	);
};

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const Row = styled.tr``;

const RowHeader = styled.tr`
	background-color: #f9fafb;
`;

const ID = styled.td`
	font-size: 16px;
	color: #333333;
	padding: 12px;
	text-align: left;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const IDHeader = styled.th`
	font-size: 16px;
	color: #333333;
	padding: 12px;
	text-align: left;
	font-weight: 700;
	white-space: nowrap;
`;

const View = styled.td`
	font-size: 16px;
	color: ${colours.orange};
	padding: 12px;
	font-weight: 500;
	white-space: nowrap;
`;
